import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Button } from "@app/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import { Input } from "@app/components/ui/input";
import { Label } from "@app/components/ui/label";
import { Textarea } from "@app/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@app/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@app/components/ui/radio-group";
import API from "@app/services/api";
import { fetchShelf, type DatasetFolder } from "@app/utils/dataset-folders";
import type { DatasetPurpose } from "@app/utils/reference-dataset";
import { Trans, useLingui } from "@lingui/macro";

/** The value the select uses for « Sans classeur » — Radix forbids an empty one. */
const NO_FOLDER = "__none__";

export default function DatasetNewPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  // Chosen here and never edited: a work dataset's dossiers have been read while tuning.
  const [purpose, setPurpose] = useState<DatasetPurpose>("work");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Opened from inside a classeur, the new dataset is filed there.
  const [searchParams] = useSearchParams();
  const [folderId, setFolderId] = useState(
    searchParams.get("classeur") ?? NO_FOLDER,
  );
  const [folders, setFolders] = useState<DatasetFolder[]>([]);

  useEffect(() => {
    fetchShelf()
      .then((shelf) => setFolders(shelf.folders))
      .catch(() => setFolders([]));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError(t`Le nom du jeu de données est requis`);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await API.post({
        path: "/dataset/",
        body: {
          name: name.trim(),
          description: description.trim() || null,
          folder_id: folderId === NO_FOLDER ? null : folderId,
          purpose,
        },
      });

      if (response.ok) {
        // Redirect to dataset detail page
        navigate(`/datasets/${response.data.id}`);
      } else {
        setError(
          response.error || t`Erreur lors de la création du jeu de données`,
        );
      }
    } catch (err) {
      setError(t`Erreur lors de la création du jeu de données`);
      console.error("Error creating dataset:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="max-w-2xl mx-auto">
        <Link to="/datasets" className="text-sm text-primary hover:underline">
          <Trans>← Retour aux jeux de données</Trans>
        </Link>

        <h1 className="text-3xl font-bold mt-4 mb-6">
          <Trans>Créer un jeu de données</Trans>
        </h1>

        <Card>
          <CardHeader>
            <CardTitle><Trans>Informations du jeu de données</Trans></CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="name">
                  <Trans>Nom du jeu de données</Trans> <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  placeholder={t`Ex. : Masson`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={loading}
                />
                <p className="text-sm text-slate-500">
                  <Trans>Le nom doit être unique et descriptif</Trans>
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description"><Trans>Description</Trans></Label>
                <Textarea
                  id="description"
                  placeholder={t`Décrivez le contenu et l'objectif de ce jeu de données...`}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  disabled={loading}
                />
              </div>

              <fieldset className="space-y-2">
                <legend className="text-sm font-medium leading-none">
                  <Trans>Usage</Trans>
                </legend>
                <RadioGroup
                  value={purpose}
                  onValueChange={(value) => setPurpose(value as DatasetPurpose)}
                  className="grid gap-3 sm:grid-cols-2"
                  disabled={loading}
                >
                  <Label
                    htmlFor="purpose-work"
                    className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-4 font-normal hover:border-primary/50 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
                  >
                    <RadioGroupItem id="purpose-work" value="work" className="mt-0.5" />
                    <span className="space-y-1">
                      <span className="block font-semibold text-slate-900">
                        <Trans>Travail</Trans>
                      </span>
                      <span className="block text-sm text-slate-600">
                        <Trans>
                          On y règle la lecture : exemples, expériences, runs et
                          corrections restent consultables.
                        </Trans>
                      </span>
                    </span>
                  </Label>
                  <Label
                    htmlFor="purpose-reference"
                    className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-4 font-normal hover:border-primary/50 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
                  >
                    <RadioGroupItem id="purpose-reference" value="reference" className="mt-0.5" />
                    <span className="space-y-1">
                      <span className="block font-semibold text-slate-900">
                        <Trans>Référence</Trans>
                      </span>
                      <span className="block text-sm text-slate-600">
                        <Trans>
                          On y mesure la lecture : on l'annote, puis on le scelle.
                          Scellé, il ne montre plus que des scores agrégés.
                        </Trans>
                      </span>
                    </span>
                  </Label>
                </RadioGroup>
                <p className="text-sm text-slate-500">
                  <Trans>
                    L'usage ne se change plus après la création. Les dossiers d'une
                    référence n'entrent dans aucun jeu de travail, et inversement.
                  </Trans>
                </p>
              </fieldset>

              {folders.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="folder">
                    <Trans>Classeur</Trans>
                  </Label>
                  <Select
                    value={folderId}
                    onValueChange={setFolderId}
                    disabled={loading}
                  >
                    <SelectTrigger id="folder">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_FOLDER}>
                        <Trans>Sans classeur</Trans>
                      </SelectItem>
                      {folders.map((folder) => (
                        <SelectItem key={folder.id} value={folder.id}>
                          {folder.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button type="submit" disabled={loading}>
                  {loading ? t`Création...` : t`Créer le jeu de données`}
                </Button>
                <Link
                  to={
                    folderId === NO_FOLDER
                      ? "/datasets"
                      : `/datasets?classeur=${folderId}`
                  }
                >
                  <Button type="button" variant="outline" disabled={loading}>
                    <Trans>Annuler</Trans>
                  </Button>
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
